/* eslint-disable no-dupe-class-members */
import get from "lodash.get";
import { roundToCents, add, subtract } from "../../../utils/mathUtils";

const toleranceMap = {
  freightWarehousing: 175,
  discount: 1,
};

/**
 * Round invoice value to 2 decimal places (cents)
 * Uses decimal.js for precision via mathUtils
 * @param {number|string|null|undefined} value - Value to round
 * @returns {number} Value rounded to 2 decimal places
 */
export const roundInvoiceVal = value => {
  return roundToCents(value);
};

export default class DataRow {
  /**
   * @type {string}
   */
  #row;
  /**
   * @type {number}
   */
  #assetsPaid;
  /**
   * @type {number}
   */
  #depositsPaid;
  /**
   * @type {number}
   */
  #otherAssetsPaid;
  /**
   * @type {number}
   */
  #otherDepositsPaid;
  /**
   * @type {number}
   */
  #maximum;
  constructor(invoice, row) {
    this.#row = row;

    this.#maximum = get(invoice, `${row}Maximum`, 0);

    this.#assetsPaid = get(invoice, `${row}AssetsPaid`, 0);
    this.#depositsPaid = get(invoice, `${row}DepositsPaid`, 0);

    this.#otherAssetsPaid = get(invoice, `${row}OtherAssetsPaid`, 0);
    this.#otherDepositsPaid = get(invoice, `${row}OtherDepositsPaid`, 0);
  }

  get assetsPaid() {
    return roundInvoiceVal(this.#assetsPaid);
  }

  set assetsPaid(value) {
    this.#assetsPaid = value;
  }

  get depositsPaid() {
    return roundInvoiceVal(this.#depositsPaid);
  }

  set depositsPaid(value) {
    this.#depositsPaid = value;
  }

  get net() {
    return roundInvoiceVal(add(this.#assetsPaid, this.#depositsPaid));
  }

  get assetsToDate() {
    return roundInvoiceVal(add(this.#assetsPaid, this.#otherAssetsPaid));
  }

  get depositsToDate() {
    return roundInvoiceVal(add(this.#depositsPaid, this.#otherDepositsPaid));
  }

  get netToDate() {
    return roundInvoiceVal(add(this.assetsToDate, this.depositsToDate));
  }

  get notProcessed() {
    const notProcessed = this.rawNotProcessed;
    const notProcessedWithTolerance = add(this.#tolerance, notProcessed);

    if (
      notProcessedWithTolerance >= 0 &&
      notProcessedWithTolerance <= this.#tolerance
    ) {
      return 0;
    }

    return roundInvoiceVal(notProcessed);
  }

  get rawNotProcessed() {
    return roundInvoiceVal(subtract(this.#maximum, this.netToDate));
  }

  get #hasTolerance() {
    return Boolean(toleranceMap[this.#row]);
  }

  get #tolerance() {
    if (this.#hasTolerance) {
      return this.#maximum == 0 ||
        this.rawNotProcessed >= -toleranceMap[this.#row]
        ? toleranceMap[this.#row]
        : 0;
    }

    return 0;
  }

  get notProcessedAssetsPaid() {
    const tolerance = this.#hasTolerance ? toleranceMap[this.#row] : 0;
    return roundInvoiceVal(
      subtract(
        add(tolerance, this.#maximum),
        subtract(this.netToDate, this.assetsPaid)
      )
    );
  }

  get notProcessedDepositsPaid() {
    const tolerance = this.#hasTolerance ? toleranceMap[this.#row] : 0;
    return roundInvoiceVal(
      subtract(
        add(tolerance, this.#maximum),
        subtract(this.netToDate, this.depositsPaid)
      )
    );
  }
}
